import { ILabelValue } from '../../interfaces/generic'
import { Track } from '../../classes/track'
import { subDays, set } from 'date-fns'

const MAX_GENRES = 8
const MAX_GENRE_LENGTH = 15
const MAX_TOP_ARTISTS = 8
const MAX_BPM = 215
const MIN_BPM = 50

export function getTopArtists(tracks: Track[]): ILabelValue[] {
  const artistsCount: { [key: string]: number } = {}

  for (const track of tracks) {
    artistsCount[track.artist || ''] = 1 + (artistsCount[track.artist] || 0)
  }

  const artistsSorted: string[] = Object.keys(artistsCount)
    .sort((a, b) => artistsCount[b] - artistsCount[a])
    .filter(a => !!a && a.trim())
    .filter(a => !a.toLowerCase().includes('various'))
    .filter(a => a.toLowerCase().trim() !== 'unknown')
    .slice(0, MAX_TOP_ARTISTS)

  return artistsSorted.map(a => {
    return {
      label: a,
      value: artistsCount[a]
    }
  })
}

export function getTopGenres(tracks: Track[]): ILabelValue[] {
  const genreCount: { [key: string]: number } = {}
  const genreCountClean: { [key: string]: number } = {}

  for (const track of tracks) {
    genreCount[track.genre || ''] = 1 + (genreCount[track.genre] || 0)
  }

  for (const genre of Object.keys(genreCount)) {
    let genreClean = genre.slice(0, MAX_GENRE_LENGTH)

    if (genre.length > MAX_GENRE_LENGTH) genreClean += '...'
    if (!genre) genreClean = 'None'

    genreClean += ` (${genreCount[genre]})`

    genreCountClean[genreClean] = 100 / (tracks.length / genreCount[genre])
  }

  const genresSorted: string[] = Object.keys(genreCountClean)
    .sort((a, b) => genreCountClean[b] - genreCountClean[a])
    .slice(0, MAX_GENRES)

  const topGenres = genresSorted.map(g => {
    return {
      label: g,
      value: genreCountClean[g]
    }
  })

  if (Object.keys(genreCountClean.length > MAX_GENRES)) {
    const totalIncludedPercentage = topGenres.reduce((total, lv) => total + lv.value, 0)
    const totalIncluded = tracks.length * (totalIncludedPercentage / 100)

    topGenres.push({
      label: `... (${Math.round(tracks.length - totalIncluded)})`,
      value: 100 - totalIncludedPercentage
    })
  }

  return topGenres
}

export function getBpmDistribution(tracks: Track[]): ILabelValue[] {
  const bpmCount: { [key: number]: number } = {}

  for (const track of tracks) {
    const bpm: number = Math.round(track.bpm || 0)

    if (bpm >= MIN_BPM && bpm <= MAX_BPM) {
      if (!bpmCount.hasOwnProperty(bpm)) bpmCount[bpm] = 0

      bpmCount[bpm] += 1
    }
  }

  const bpmBandwidths: { [key: number]: number } = {}

  for (const key of Object.keys(bpmCount)) {
    const bpm = parseInt(key, 10)

    const rangeStart = Math.floor(bpm / 5) * 5

    if (!bpmBandwidths.hasOwnProperty(rangeStart)) bpmBandwidths[rangeStart] = 0

    bpmBandwidths[rangeStart] += (bpmCount[bpm] || 0)
  }

  for (let n = MIN_BPM; n <= MAX_BPM; n += 5) {
    if (!bpmBandwidths.hasOwnProperty(n)) {
      bpmBandwidths[n] = 0
    }
  }

  return Object.keys(bpmBandwidths).map(rangeStart => {
    const r = parseInt(rangeStart, 10)

    return {
      label: `${rangeStart.toString()} - ${(r + 5).toString()}`,
      value: bpmBandwidths[r]
    }
  })
}

export function getMostPlayedArtists(tracks: Track[]): ILabelValue[] {
  const artistsCount: { [key: string]: number } = {}

  for (const track of tracks) {
    artistsCount[track.artist || ''] = (track.playCount || 0) + (artistsCount[track.artist] || 0)
  }

  const artistsSorted: string[] = Object.keys(artistsCount)
    .sort((a, b) => artistsCount[b] - artistsCount[a])
    .filter(a => artistsCount[a] !== 0)
    .filter(a => !!a)
    .filter(a => !a.toLowerCase().includes('various'))
    .filter(a => a.toLowerCase().trim() !== 'unknown')
    .slice(0, MAX_TOP_ARTISTS)

  return artistsSorted.map(a => {
    return {
      label: a,
      value: artistsCount[a]
    }
  })
}

export function getNewestArtists(tracks: Track[]): ILabelValue[] {
  const artistsCount: { [key: string]: number } = {}

  const timestamp30DaysAgo = subDays(new Date(), 30)

  for (const track of tracks.filter(t => new Date(t.dateImported).getTime() > timestamp30DaysAgo.getTime())) {
    artistsCount[track.artist || ''] = 1 + (artistsCount[track.artist] || 0)
  }

  const artistsSorted: string[] = Object.keys(artistsCount)
    .sort((a, b) => artistsCount[b] - artistsCount[a])
    .filter(a => !!a)
    .filter(a => !a.toLowerCase().includes('various'))
    .filter(a => a.toLowerCase().trim() !== 'unknown')
    .slice(0, MAX_TOP_ARTISTS)

  return artistsSorted.map(a => {
    return {
      label: a,
      value: artistsCount[a]
    }
  })
}

export function getTracksImportedTimes(tracks: Track[]): ILabelValue[] {
  const timestamp7DaysAgo: number = subDays(new Date(), 7).getTime()
  const timestamp30DaysAgo: number = subDays(new Date(), 30).getTime()
  const timestampThisYear: number = set(new Date(), {
    month: 0,
    date: 1,
    hours: 0,
    minutes: 0,
    seconds: 0
  }).getTime()

  const timestampCount: { [key: string]: number } = {
    LAST_7_DAYS: 0,
    LAST_30_DAYS: 0,
    THIS_YEAR: 0
  }

  for (const track of tracks) {
    const time: number = new Date(track.dateImported).getTime()

    if (time > timestamp7DaysAgo) timestampCount.LAST_7_DAYS++
    if (time > timestamp30DaysAgo) timestampCount.LAST_30_DAYS++
    if (time > timestampThisYear) timestampCount.THIS_YEAR++
  }

  return [
    {
      label: 'Last 7 days',
      value: timestampCount.LAST_7_DAYS
    },
    {
      label: 'Last 30 days',
      value: timestampCount.LAST_30_DAYS
    },
    {
      label: 'This year',
      value: timestampCount.THIS_YEAR
    }
  ]
}
